import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Buttons, MenuButton } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";

import { useListMemberSeparableAccumulationsQuery } from "@/App/api/member";
import { ThemedButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { DisplayScopes } from "@/App/types/accumulation";
import { useAccumulations } from "@/App/hooks/accumulations";
import { AccumulatorWidgetCard } from "@/App/pages/PlanContributions/Accumulations/AccumulatorWidgetCard";
import { isFamilyCoverage } from "@/App/utils/familyCoverage";

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard.planContributions";

export const PlanContributionsWidget = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();

  const { data: accumulations, isLoading: isLoadingAccumulations } = useListMemberSeparableAccumulationsQuery(true);
  const { inNetworkAccumulations, individualAccumulations, familyAccumulations } =
    useAccumulations();

  const scopes = {
    individual: translate(`${TRANSLATION_PATH}.displayScopeIndividual`),
    family: translate(`${TRANSLATION_PATH}.displayScopeFamily`),
  };

  const [displayScope, setDisplayScope] = useState<DisplayScopes>(isFamilyCoverage ? DisplayScopes.Family : DisplayScopes.Individual);

  const displayScopeToggleVisible = useMemo(() => {
    return !!(individualAccumulations(accumulations?.results)?.length && familyAccumulations(accumulations?.results)?.length);
  }, [accumulations, individualAccumulations, familyAccumulations]);


  const accumulationsToDisplay = useMemo(() => {
    if (!isLoadingAccumulations) {
      if (displayScope === DisplayScopes.Individual) {
        return individualAccumulations(inNetworkAccumulations(accumulations.results));
      } else if (displayScope === DisplayScopes.Family) {
        return familyAccumulations(inNetworkAccumulations(accumulations.results));
      }
    }
  }, [inNetworkAccumulations, individualAccumulations, familyAccumulations, displayScope, accumulations]);

  const deductibleAccum = useMemo(() => {
    return accumulationsToDisplay?.filter((accum) => accum.type === "deductible");
  }, [accumulationsToDisplay]);

  const outOfPocketAccum = useMemo(() => {
    return accumulationsToDisplay?.filter((accum) => accum.type === "maximum_out_of_pocket");
  }, [accumulationsToDisplay]);

  const { isMobile } = useResponsive({
    isMobile: {
      mobile: true,
      desktop: false,
      tablet: false,
    },
  });

  const onChangeScopeDisplayType = useCallback((value: string) => {
    if (value === DisplayScopes.Family) {
      setDisplayScope(DisplayScopes.Family);
      logEvent(ANALYTIC_EVENTS.MY_RX.familyWidget);
    } else {
      setDisplayScope(DisplayScopes.Individual);
      logEvent(ANALYTIC_EVENTS.MY_RX.individualWidget);
    }
  }, []);

  const viewAll = useCallback(() => {
    navigate(`/plan-contributions`);
  }, []);

  return (
    <Stack spacing="large">
      <Shelf>
        <Heading>{translate(`${TRANSLATION_PATH}.inNetwork`)}</Heading>
        <Shelf.Spacer />
        {displayScopeToggleVisible && (
          <MenuButton appearance={Buttons.Functional.Tertiary.Large} label={scopes[displayScope]} caretIcon>
            {displayScope !== DisplayScopes.Individual && (
              <MenuButton.Item onClick={() => onChangeScopeDisplayType(DisplayScopes.Individual)}>
                {scopes[DisplayScopes.Individual]}
              </MenuButton.Item>
            )}
            {displayScope !== DisplayScopes.Family && (
              <MenuButton.Item onClick={() => onChangeScopeDisplayType(DisplayScopes.Family)}>
                {scopes[DisplayScopes.Family]}
              </MenuButton.Item>
            )}
          </MenuButton>
        )}
      </Shelf>
      <Stack spacing="medium">
        {!!deductibleAccum?.length && <AccumulatorWidgetCard displayScope={displayScope} accumulator={deductibleAccum[0]} />}
        {!!outOfPocketAccum?.length && <AccumulatorWidgetCard displayScope={displayScope} accumulator={outOfPocketAccum[0]} />}
      </Stack>
      <Shelf>
        <ThemedButton appearance="text" onClick={viewAll}>
          {translate(`${TRANSLATION_PATH}.viewAll`)}
        </ThemedButton>
        {!isMobile && <Shelf.Spacer />}
      </Shelf>
    </Stack>
  );
};
